// src/features/passwords/hooks/usePasswordFetch.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../../api';

/**
 * Fetch passwords, ports, and users. Builds a convenient usersMap:
 * usersMap[user_code] = {
 *   base: {...},                // base row (no email) — synthesized if missing
 *   [emailAddress]: {...},      // per-email overrides
 * }
 *
 * Options:
 *   - advice: boolean (default false) → uses /advice-passwords/ endpoints
 *   - skip: number (default 0) → pagination offset
 *   - limit: number (default 500) → records per page
 *   - searchTerm: string (default '') → search query for user_code
 */
export default function usePasswordFetch(options = {}) {
  const {
    advice = false,
    skip = 0,
    limit = 500,
    searchTerm = '',
  } = options;
  
  const [passwords, setPasswords] = useState([]);
  const [ports, setPorts] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portQueueStatus, setPortQueueStatus] = useState({});
  const [totalCount, setTotalCount] = useState(0);

  // keep latest options in a ref to avoid stale closures during in-flight requests
  const optsRef = useRef({ advice, skip, limit, searchTerm });
  optsRef.current = { advice, skip, limit, searchTerm };

  const passwordsEndpoint = advice ? '/advice-passwords/' : '/passwords/';
  const countEndpoint = advice ? '/advice-passwords/count' : '/passwords/count';

  /**
   * Normalize one API user row to the fields our UI expects.
   * Accepts both `DOB` and `dob`.
   */
  const normalizeUserRow = (row) => {
    const dobVal = row?.DOB ?? row?.dob ?? '';
    const multilogin =
      Object.prototype.hasOwnProperty.call(row || {}, 'multilogin')
        ? Boolean(row.multilogin)
        : undefined;

    return {
      // base-ish fields
      full_name: row?.full_name || '',
      notes: row?.notes || '',
      referral: row?.referral || '',
      slack_user_id: row?.slack_user_id || '',
      crm_password: row?.crm_password || '',
      address: row?.address || '',
      dob: dobVal || '',
      subscribed: Boolean(row?.subscribed),
      onboarded: Boolean(row?.onboarded),

      // email-specific (only meaningful on per-email keys)
      email_specific_name: row?.email_specific_name || '',
      email_specific_dob: row?.email_specific_dob || '',
      email_specific_address: row?.email_specific_address || '',

      // optional
      ...(multilogin !== undefined ? { multilogin } : {}),
    };
  };

  /**
   * Build users map. If a user has no explicit `base` record,
   * synthesize it from the first per-email record so the parent row
   * (Slack ID, referral, crm_password, subscribed/onboarded, etc.) shows up.
   */
  const buildUsersMap = useCallback((rows) => {
    const map = {};

    // First pass: place everything by key (email or 'base')
    for (const row of rows || []) {
      const code = row?.user_code || '';
      if (!code) continue;

      if (!map[code]) map[code] = {};

      const key =
        row?.email && String(row.email).trim() !== '' ? row.email : 'base';

      map[code][key] = normalizeUserRow(row);
    }

    // Second pass: ensure a base row exists, synthesizing from the first email row if necessary
    for (const [code, perUser] of Object.entries(map)) {
      if (!perUser.base) {
        const firstEmailKey = Object.keys(perUser).find((k) => k !== 'base');
        if (firstEmailKey) {
          const fromEmail = perUser[firstEmailKey] || {};

          // Create a base with the global-ish fields copied over
          perUser.base = {
            full_name: fromEmail.full_name || '',
            notes: fromEmail.notes || '',
            referral: fromEmail.referral || '',
            slack_user_id: fromEmail.slack_user_id || '',
            crm_password: fromEmail.crm_password || '',
            address: fromEmail.address || '',
            dob: fromEmail.dob || '',
            subscribed: Boolean(fromEmail.subscribed),
            onboarded: Boolean(fromEmail.onboarded),
            ...(Object.prototype.hasOwnProperty.call(fromEmail, 'multilogin')
              ? { multilogin: fromEmail.multilogin }
              : {}),
          };
        } else {
          // No records at all (shouldn't happen often), make an empty base
          perUser.base = {
            full_name: '',
            notes: '',
            referral: '',
            slack_user_id: '',
            crm_password: '',
            address: '',
            dob: '',
            subscribed: false,
            onboarded: false,
          };
        }
      }
    }

    return map;
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Allow cancellation if component unmounts mid-flight
    const controller = new AbortController();

    try {
      const params = {
        skip: Number(skip) || 0,
        limit: Math.min(500, Math.max(1, Number(limit) || 500)),
      };

      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const countParams = { ...params };
      delete countParams.skip;
      delete countParams.limit;

      const [passwordsRes, countRes, portsRes, usersRes, queueRes] = await Promise.all([
        api.get(passwordsEndpoint, { params, signal: controller.signal }),
        api.get(countEndpoint, { params: countParams, signal: controller.signal }),
        api.get('/ports/', { signal: controller.signal }),
        api.get('/users/', { signal: controller.signal }),
        api.get('/api/ports/queue', { signal: controller.signal }),
      ]);

      const queueData = queueRes?.data?.queues || {};

      const portQueueStatus = {};
      Object.values(queueData).flat().forEach((item) => {
        if (item.target_port_id) {
          portQueueStatus[item.target_port_id] = {
            status: item.status,
            position: item.position,
            eta_seconds: item.eta_seconds,
          };
        }
      });

      if (
        optsRef.current.advice !== advice ||
        optsRef.current.skip !== skip ||
        optsRef.current.limit !== limit ||
        optsRef.current.searchTerm !== searchTerm
      ) {
        return;
      }

      setPasswords(passwordsRes.data || []);
      setTotalCount(countRes?.data?.total ?? 0);
      setPorts(portsRes.data || []);
      setUsersMap(buildUsersMap(usersRes.data || []));
      setPortQueueStatus(portQueueStatus);
    } catch (err) {
      if (err?.name === 'CanceledError') return;
      if (err?.code === 'ERR_CANCELED') return;

      const detail =
        err?.response?.data?.detail || err?.message || 'Failed to load data';
      setError(detail);
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  }, [advice, skip, limit, searchTerm, passwordsEndpoint, countEndpoint, buildUsersMap]);

  useEffect(() => {
    let canceled = false;
    (async () => {
      if (!canceled) {
        await fetchAll();
      }
    })();
    return () => {
      canceled = true;
    };
  }, [fetchAll]);

  const refetch = useCallback(() => fetchAll(), [fetchAll]);

  const data = useMemo(
    () => ({ passwords, ports, usersMap, portQueueStatus, totalCount }),
    [passwords, ports, usersMap, portQueueStatus, totalCount]
  );

  return { ...data, loading, error, refetch };
}
